import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { MDBInput, MDBTextArea } from "mdb-react-ui-kit";
import { TextInput } from "../../../components/core/inputs/TextInput";
import { NumberInput } from "../../../components/core/inputs/NumberInput";
import { TextAreaInput } from "../../../components/core/inputs/TextAreaInput";
import { Alert } from "../../../components/Alert";

export interface ProductEditProps {
    productId: number;
    canEdit: boolean;
    canDelete: boolean;
    onAdd: () => void;
    onEdit: () => void;
    onClose: () => void;
}

export const ProductEdit = ({productId, canEdit, canDelete, onAdd, onEdit, onClose}: ProductEditProps) => {
    const { t } = useTranslation();
    const [product, setProduct] = useState<any|null>(null);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [changed, setChanged] = useState(false);
    const [deleteProduct, setDeleteProduct] = useState(false);
    

    const fetch = useCallback(() => {
        setError('');
        setChanged(false);
        if (productId >= 0) {
            setLoading(true);
            API.GET('/api/products/'+productId)
                .then(p => { setProduct(p); setName(p.name); })
                .catch(err => setError(err.message))
                .finally(() => setLoading(false));
        }else {
            setProduct({
                name: '', description: '', aqPrice: undefined, deleted: null
            });
        }
    }, [productId]);

    useEffect(() => {
        fetch();
    }, [productId]);

    const changeProduct = useCallback((key: string, val: any) => {
        setProduct(prev => ({
            ...prev,
            [key]: val
        }));
        setChanged(true);
        setError('');
    }, []);

    const update = useCallback(() => {
        setLoading(true);
        API.PATCH('/api/products/'+productId, {
            name: product.name,
            description: product.description,
            aqPrice: product.aqPrice
        })
            .then(() => { fetch(); onEdit(); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [productId, product]);

    const insert = useCallback(() => {
        setLoading(true);
        API.POST('/api/products', {
            name: product.name,
            description: product.description,
            aqPrice: product.aqPrice
        })
            .then(() => onAdd())
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [product]);

    if (!product) return null;

    return (
        <div className="d-flex flex-column overflow-hidden card">
            <button type="button" className="btn-close position-absolute" style={{right: 5, top: 5}} onClick={() => onClose ? onClose() : {}}></button>
            <DeleteConfirmModal
                open={deleteProduct}
                title={t('remove_product')}
                message={t('remove_product_prompt')}
                onCancel={() => setDeleteProduct(false)}
                onConfirm={() => {
                    API.DELETE('/api/products/'+productId)
                        .then(() => { onEdit(); onClose(); })
                    setDeleteProduct(false);
                }}
            />
            <div className="d-flex flex-column overflow-hidden card-body">
                <div className="d-flex flex-column w-100 justify-content-center overflow-hidden">
                    <i className="fas fa-shopping-cart fa-4x mb-2"/>
                    
                    
                    {loading && <LoadingSpinner/>}
                    {!loading && <>
                        <h5 className="text-center">{productId == -1 ? t('new_product') : name}</h5>
                        
                        <div className="d-flex flex-column overflow-auto pe-1 pt-2">
                            { (productId !== -1 && !canEdit) && <>
                                <ul className="list-group list-group-light p-0">
                                    <li className="list-group-item p-1"><i className="fas fa-circle-info fa-fw me-1"/><span className="fw-bold me-1">{t('description')}:</span>{product.description}</li>
                                    <li className="list-group-item p-1"><i className="fas fa-coins fa-fw me-1"/><span className="fw-bold me-1">{t('aqprice')}:</span>{product.aqPrice}</li>
                                </ul>
                            </> }

                            { (productId == -1 || canEdit) && <>
                                <TextInput label={t('name')} icon="pen-to-square" value={product.name} onValueChange={val => changeProduct('name', val)}/>
                                <TextAreaInput label={t('description')} icon="circle-info" rows={3} value={product.description} onValueChange={val => changeProduct('description', val)}/>
                                <NumberInput label={t('aqprice')} icon="coins" value={product.aqPrice} onValueChange={val => changeProduct('aqPrice', val)}/>
                            </> }
                            <Alert type="danger" message={error}/>

                            {productId !== -1 && <>
                                { canEdit && <button className="btn btn-primary mb-2" disabled={!changed || loading} onClick={() => update()}>{t('save')}</button> }
                                { canDelete && <button className="btn btn-danger" disabled={loading} onClick={() => setDeleteProduct(true)}>{t('delete')}</button> }
                            </>}
                            {productId === -1 && <>
                                <button className="btn btn-primary mt-2" disabled={!changed || loading} onClick={() => insert()}>{t('add')} <i className="far fa-square-plus fa-lg"/></button>
                            </>}
                        </div>
                        

                    </>}
                </div>
            </div>
        </div>
    )

}